/**
 * LegalLayout — extends MarketingLayout with a reading-optimised content column.
 *
 * Max-width 720px content column, body text 19px Inter, line-height 1.7.
 * h2 headings in Newsreader serif.
 * Last-updated timestamp (brass-600 mono) rendered at the top.
 * Optional table-of-contents anchor list for long pages.
 */

import type { ReactNode } from 'react';
import { MarketingLayout } from './marketing-layout';

interface TocEntry {
  anchor: string;
  label: string;
}

interface LegalLayoutProps {
  heading: string;
  lastUpdated: string;
  toc?: TocEntry[];
  children: ReactNode;
}

export function LegalLayout({ heading, lastUpdated, toc, children }: LegalLayoutProps) {
  return (
    <MarketingLayout>
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '56px 24px 96px',
        }}
      >
        {/* Last updated */}
        <div
          className="mono"
          style={{
            fontSize: 12.5,
            color: 'var(--accent-600)',
            marginBottom: 20,
            letterSpacing: '-0.005em',
          }}
        >
          Last updated {lastUpdated}
        </div>

        {/* Page heading */}
        <h1
          className="serif"
          style={{
            fontSize: 44,
            fontStyle: 'italic',
            fontWeight: 300,
            color: 'var(--ink)',
            margin: '0 0 40px',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
          }}
        >
          {heading}
        </h1>

        {/* Table of contents */}
        {toc && toc.length > 0 && (
          <nav
            aria-label="Table of contents"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--hairline)',
              borderRadius: 'var(--r-md)',
              padding: '20px 24px',
              marginBottom: 48,
            }}
          >
            {/* BEH-27, round-3 add-on (2026-09-22): same bare `.eyebrow`-defaults-
                to-gold pattern as `FooterLinkGroup` in marketing-layout.tsx —
                this TOC label is on every /legal/* page, never inside a hero. */}
            <div className="eyebrow ink" style={{ marginBottom: 12 }}>
              Contents
            </div>
            <ol
              style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {toc.map((entry, i) => (
                <li key={entry.anchor} style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                  <span
                    className="mono"
                    style={{ fontSize: 11, color: 'var(--ink-4)', minWidth: 20 }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <a
                    href={`#${entry.anchor}`}
                    style={{
                      fontSize: 14,
                      color: 'var(--primary-600)',
                      textDecoration: 'none',
                      borderBottom: '1px solid rgba(31,77,69,0.2)',
                      paddingBottom: 1,
                    }}
                  >
                    {entry.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* Body */}
        <div className="legal-body">{children}</div>
      </div>

      {/* Scoped prose styles */}
      <style>{`
        .legal-body { font-size: 19px; line-height: 1.7; color: var(--ink-2); }
        .legal-body h2 {
          font-family: var(--serif);
          font-style: italic;
          font-weight: 300;
          font-size: 26px;
          color: var(--ink);
          margin: 48px 0 16px;
          letter-spacing: -0.01em;
          line-height: 1.2;
          scroll-margin-top: 80px;
        }
        .legal-body h3 {
          font-size: 16px;
          font-weight: 600;
          color: var(--ink);
          margin: 28px 0 10px;
          letter-spacing: -0.01em;
        }
        .legal-body p { margin: 0 0 18px; }
        .legal-body ul, .legal-body ol { margin: 0 0 18px; padding-left: 24px; }
        .legal-body li { margin-bottom: 8px; }
        .legal-body a { color: var(--primary-600); }
        .legal-body strong { color: var(--ink); font-weight: 600; }
        .legal-body .legal-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 15px;
          margin: 0 0 28px;
        }
        .legal-body .legal-table th {
          text-align: left;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--ink-3);
          padding: 8px 0 12px;
          border-bottom: 1px solid var(--hairline);
        }
        .legal-body .legal-table td {
          padding: 14px 0;
          border-bottom: 1px solid var(--hairline);
          vertical-align: top;
          color: var(--ink-2);
          font-size: 15px;
          line-height: 1.55;
        }
        .legal-body .legal-table tr:last-child td { border-bottom: none; }
        .legal-body .legal-table td:not(:last-child) { padding-right: 24px; }
        /* Design gate F1: on a phone a five-column table scrolls inside its own
           frame; the page never scrolls sideways. */
        @media (max-width: 720px) {
          .legal-body .legal-table { display: block; overflow-x: auto; -webkit-overflow-scrolling: touch; }
        }
        .legal-info-box {
          background: var(--surface);
          border: 1px solid var(--hairline);
          border-radius: var(--r-md);
          padding: 20px 24px;
          margin: 0 0 28px;
          font-size: 15px;
          line-height: 1.6;
        }
        .legal-info-box .eyebrow { margin-bottom: 8px; }
      `}</style>
    </MarketingLayout>
  );
}
